<template>
  <v-row class="detail-info-grid">
    <!-- Grouped: 3 columns, each column has multiple fields stacked -->
    <template v-if="groups && groups.length > 0">
      <v-col
        v-for="(group, colIndex) in groups"
        :key="colIndex"
        :cols="groupCols"
        class="d-flex flex-column">
        <div
          v-for="(field, fieldIndex) in group"
          :key="fieldIndex"
          class="detail-info-field w-full d-flex flex-column">
          <div class="d-flex align-center flex-wrap">
            <small>{{ field.label }}</small>
            <slot
              v-if="field.labelSlot"
              :name="field.labelSlot"
              :field="field" />
          </div>
          <!-- text -->
          <div
            v-if="field.type === 'text' || !field.type"
            class="detail-info-field__value indigo--text font-weight-bold">
            {{ field.value != null && field.value !== '' ? field.value : '-' }}
          </div>
          <!-- link -->
          <div v-else-if="field.type === 'link'" class="detail-info-field__value">
            <span
              v-if="field.value"
              class="detail-link"
              @click="field.onClick && field.onClick()">
              {{ field.value }}
            </span>
            <div v-else class="indigo--text font-weight-bold">-</div>
          </div>
          <!-- withIcon (e.g. Status) -->
          <div v-else-if="field.type === 'withIcon'" class="detail-info-field__value d-flex align-center">
            <v-icon size="small" v-if="field.icon" class="mr-1" :color="field.iconColor" :class="field.iconClass">
              {{ field.icon }}
            </v-icon>
            <span class="indigo--text font-weight-bold">
              {{ field.value != null && field.value !== '' ? field.value : '-' }}
            </span>
          </div>
          <!-- custom: slot renders content -->
          <div v-else-if="field.type === 'custom' && field.slot" class="detail-info-field__value">
            <slot
              :name="`field-${field.slot}`"
              :field="field" />
          </div>
        </div>
      </v-col>
    </template>
    <!-- Flat: each field is one cell (cols="4"), 3 per row -->
    <template v-else-if="fields && fields.length > 0">
      <v-col
        v-for="(field, index) in fields"
        :key="index"
        cols="4"
        class="d-flex flex-column detail-info-field">
        <div class="d-flex align-center flex-wrap">
          <small>{{ field.label }}</small>
          <slot
            v-if="field.labelSlot"
            :name="field.labelSlot"
            :field="field" />
        </div>
        <div
          v-if="field.type === 'text' || !field.type"
          class="detail-info-field__value indigo--text font-weight-bold">
          {{ field.value != null && field.value !== '' ? field.value : '-' }}
        </div>
        <div v-else-if="field.type === 'link'" class="detail-info-field__value">
          <span
            v-if="field.value"
            class="detail-link"
            @click="field.onClick && field.onClick()">
            {{ field.value }}
          </span>
          <div v-else class="indigo--text font-weight-bold">-</div>
        </div>
        <div v-else-if="field.type === 'withIcon'" class="detail-info-field__value d-flex align-center">
          <v-icon size="small" v-if="field.icon" class="mr-1" :color="field.iconColor" :class="field.iconClass">
            {{ field.icon }}
          </v-icon>
          <span class="indigo--text font-weight-bold">
            {{ field.value != null && field.value !== '' ? field.value : '-' }}
          </span>
        </div>
        <div
          v-else-if="field.type === 'custom' && field.slot"
          class="detail-info-field__value">
          <slot
            :name="`field-${field.slot}`"
            :field="field" />
        </div>
      </v-col>
    </template>
  </v-row>
</template>

<script>
/**
 * DetailInfoGrid — 디테일 화면 Information 블록의 그리드 레이아웃 공통 컴포넌트
 *
 * 사용 방식:
 * 1) 그룹 모드 (ScheduleJob 등): groups = [ [필드들 컬럼1], [필드들 컬럼2], [필드들 컬럼3] ]
 * 2) 플랫 모드 (Serving 등): fields = [ 필드, 필드, ... ] → 각 필드가 cols="4" 한 칸
 *
 * 필드 정의:
 * - label: string (라벨)
 * - type: 'text' | 'link' | 'withIcon' | 'custom'
 * - value: string (text/link/withIcon일 때)
 * - onClick: fn (link일 때)
 * - icon, iconColor, iconClass (withIcon일 때)
 * - slot: string (custom일 때, 슬롯 이름 field-${slot} 사용)
 * - labelSlot: string (라벨 옆에 붙일 슬롯, 예: 툴팁)
 * - payload: any (custom 슬롯에 전달할 데이터)
 */
export default {
  name: 'DetailInfoGrid',
  props: {
    /** 컬럼별 필드 배열. [ [필드...], [필드...], [필드...] ] */
    groups: {
      type: Array,
      default: () => null,
    },
    /** 그룹 모드에서 컬럼당 cols (기본 4). 2열일 때 6 사용 */
    groupCols: {
      type: [Number, String],
      default: 4,
    },
    /** 플랫 필드 배열. groups 없을 때 사용, 각 필드가 한 칸(cols="4") */
    fields: {
      type: Array,
      default: () => null,
    },
  },
}
</script>
